import { ArrowDown } from '@jsluna/icons';
import { arrayIncludes, ClickAway, childMatchesType, onKeyPress } from '@jsluna/utils';
import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var ListItem = function ListItem(_ref) {
  var _classnames;

  var className = _ref.className,
      children = _ref.children,
      type = _ref.type,
      rest = _objectWithoutPropertiesLoose(_ref, ["className", "children", "type"]);

  return /*#__PURE__*/React.createElement("li", _extends({}, rest, {
    className: classnames(className, (_classnames = {}, _classnames["ln-o-" + type + "-list__item"] = type, _classnames))
  }), children);
};

ListItem.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  type: PropTypes.oneOf(['bare', 'inline', 'matrix', 'justified'])
};
ListItem.defaultProps = {
  className: undefined,
  type: undefined
};
ListItem.displayName = 'ListItem';

var List = function List(_ref) {
  var className = _ref.className,
      children = _ref.children,
      type = _ref.type,
      spaced = _ref.spaced,
      items = _ref.items,
      ordered = _ref.ordered,
      rest = _objectWithoutPropertiesLoose(_ref, ["className", "children", "type", "spaced", "items", "ordered"]);

  var ListElement = ordered ? 'ol' : 'ul';
  var keys = [];
  return /*#__PURE__*/React.createElement(ListElement, _extends({}, rest, {
    className: classnames(className, type && "ln-o-" + type + "-list", type && spaced && (typeof spaced === 'string' ? "ln-o-" + type + "-list--spaced-" + spaced : "ln-o-" + type + "-list--spaced"))
  }), items ? items.map(function (item, index) {
    var key = "listItem-" + (!arrayIncludes(keys, item.toString()) ? item : index);
    keys.push(item.toString());
    return /*#__PURE__*/React.createElement(ListItem, {
      key: key,
      type: type
    }, item);
  }) : React.Children.map(children, function (child) {
    return child && child.type && child.type === ListItem ? React.cloneElement(child, {
      type: type
    }) : child;
  }));
};

List.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  items: PropTypes.arrayOf(PropTypes.node),
  type: PropTypes.oneOf(['bare', 'inline', 'matrix', 'justified']),
  spaced: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  ordered: PropTypes.bool
};
List.defaultProps = {
  className: undefined,
  children: false,
  items: undefined,
  type: undefined,
  spaced: false,
  ordered: false
};
List.displayName = 'List';

var ListGroup = function ListGroup(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      actionable = _ref.actionable,
      type = _ref.type,
      color = _ref.color,
      inline = _ref.inline,
      size = _ref.size,
      spaced = _ref.spaced,
      hard = _ref.hard,
      open = _ref.open,
      onDropdownBlur = _ref.onDropdownBlur,
      onItemClick = _ref.onItemClick,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className", "actionable", "type", "color", "inline", "size", "spaced", "hard", "open", "onDropdownBlur", "onItemClick"]);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames('ln-c-list-group', actionable && 'ln-c-list-group--actionable', type && "ln-c-list-group--" + type, color && "ln-c-list-group--" + color, size && "ln-c-list-group--" + size, inline && (typeof inline === 'string' ? "ln-c-list-group--inline@" + inline : 'ln-c-list-group--inline'), spaced && 'ln-c-list-group--spaced', hard && 'ln-c-list-group--hard', className),
    "aria-hidden": !open || undefined
  }), open && React.Children.map(children, function (child, index) {
    var props = {};

    if (onDropdownBlur && React.Children.count(children) === index + 1) {
      props.onBlur = onDropdownBlur;
    }

    if (onItemClick) {
      props.onClick = onItemClick;
    }

    if (Object.keys(props).length) {
      return React.cloneElement(child, props);
    }

    return child;
  }));
};

ListGroup.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string,

  /** Items within the group should have hover states */
  actionable: PropTypes.bool,

  /** Change the list item style to menu or pill navigation variants */
  type: PropTypes.oneOf(['full', 'pill']),

  /** Change colour theme used */
  color: PropTypes.oneOf(['light', 'dark']),

  /** Display items in a row, can provide a breakpoint reference for responsive behaviour */
  inline: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /** Change the height of items to use `$ln-size-sm` or `$ln-size-lg` */
  size: PropTypes.oneOf(['lg', 'sm']),

  /** Adds margins between ListGroupItems */
  spaced: PropTypes.bool,

  /** Turns off padding from child ListGroupItems */
  hard: PropTypes.bool,

  /** Whether the List contents should be rendered to the page */
  open: PropTypes.bool,

  /** Event that fires on focus out of the last item of a dropdown list, can be used to close the dropdown */
  onDropdownBlur: PropTypes.func,

  /** Common onClick handler that will be applied to any nested items */
  onItemClick: PropTypes.func
};
ListGroup.defaultProps = {
  /** Allows the top-level element to be customized */
  element: 'ul',
  children: undefined,
  className: undefined,
  actionable: false,
  type: undefined,
  color: undefined,
  inline: false,
  size: undefined,
  spaced: false,
  hard: false,
  open: true,
  onDropdownBlur: undefined,
  onItemClick: undefined
};
ListGroup.displayName = 'ListGroup';

var ListGroupItem = function ListGroupItem(_ref) {
  var element = _ref.element,
      containerElement = _ref.containerElement,
      containerClassName = _ref.containerClassName,
      children = _ref.children,
      className = _ref.className,
      active = _ref.active,
      activeType = _ref.activeType,
      innerRef = _ref.innerRef,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "containerElement", "containerClassName", "children", "className", "active", "activeType", "innerRef"]);

  var ContainerElement = containerElement;
  var Element = element;
  var containerProps = ContainerElement !== Fragment ? {
    className: containerClassName
  } : {};
  return /*#__PURE__*/React.createElement(ContainerElement, containerProps, /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames('ln-c-list-group__item', active && 'is-active', className),
    "aria-current": active && !!activeType ? activeType : undefined,
    ref: innerRef
  }), children));
};

ListGroupItem.propTypes = {
  /** Allows the top-level element to be customized */
  element: PropTypes.elementType,

  /** Element that will wrap the item */
  containerElement: PropTypes.elementType,

  /** ClassName to add to the wrapping `li` if not overriden by `containerElement` */
  containerClassName: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,

  /** Should the item be highlighted within the list */
  active: PropTypes.bool,

  /** Additional context for screen readers - what does the active item relate to */
  activeType: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  innerRef: PropTypes.func
};
ListGroupItem.defaultProps = {
  element: 'span',
  containerElement: 'li',
  containerClassName: undefined,
  children: undefined,
  className: undefined,
  active: false,
  activeType: 'page',
  innerRef: undefined
};
ListGroupItem.displayName = 'ListGroupItem';

var ListGroupDropdown = /*#__PURE__*/function (_Component) {
  _inheritsLoose(ListGroupDropdown, _Component);

  function ListGroupDropdown(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.onKeyPress = onKeyPress([{
      name: 'Escape',
      code: 27
    }], function (e) {
      _this.handleEscapePress(e);
    });
    _this.listItemRef = null;
    return _this;
  }

  var _proto = ListGroupDropdown.prototype;

  _proto.componentDidMount = function componentDidMount() {
    document.addEventListener('keydown', this.onKeyPress, {
      capture: true
    });
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyPress, false);
  };

  _proto.handleEscapePress = function handleEscapePress(e) {
    var _this$props = this.props,
        onClose = _this$props.onClose,
        active = _this$props.active;

    if (onClose && active) {
      onClose(e);

      if (this.listItemRef) {
        this.listItemRef.focus();
      }
    }
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$props2 = this.props,
        element = _this$props2.element,
        className = _this$props2.className,
        containerClassName = _this$props2.containerClassName,
        children = _this$props2.children,
        active = _this$props2.active,
        onClose = _this$props2.onClose,
        onMouseEnter = _this$props2.onMouseEnter,
        onMouseLeave = _this$props2.onMouseLeave,
        hover = _this$props2.hover,
        noIcon = _this$props2.noIcon,
        position = _this$props2.position,
        font = _this$props2.font,
        defaultOpen = _this$props2.defaultOpen,
        onItemClick = _this$props2.onItemClick,
        rest = _objectWithoutPropertiesLoose(_this$props2, ["element", "className", "containerClassName", "children", "active", "onClose", "onMouseEnter", "onMouseLeave", "hover", "noIcon", "position", "font", "defaultOpen", "onItemClick"]);

    var listItemLabel = [];
    var dropdownMenu = false;
    React.Children.forEach(children, function (child) {
      if (childMatchesType(child, ListGroup)) {
        dropdownMenu = child;
      } else {
        listItemLabel.push(child);
      }
    });
    var isOpen = active || hover;
    return /*#__PURE__*/React.createElement(ClickAway, {
      onClickAway: onClose,
      inactive: !active
    }, /*#__PURE__*/React.createElement("li", {
      className: classnames(containerClassName, 'ln-c-list-group__dropdown-container'),
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave
    }, /*#__PURE__*/React.createElement(ListGroupItem, _extends({}, rest, {
      element: element,
      containerElement: Fragment,
      className: classnames(className, hover && 'is-hover'),
      active: typeof active !== 'undefined' ? active : defaultOpen,
      activeType: false,
      innerRef: function innerRef(el) {
        _this2.listItemRef = el;
      },
      "aria-haspopup": true,
      "aria-expanded": typeof isOpen !== 'undefined' ? isOpen : defaultOpen
    }), /*#__PURE__*/React.createElement(Fragment, null, listItemLabel, !noIcon && /*#__PURE__*/React.createElement(ArrowDown, {
      className: "ln-c-list-group__dropdown-arrow"
    }))), dropdownMenu && React.cloneElement(dropdownMenu, {
      className: classnames('ln-c-list-group--dropdown', position && "ln-c-list-group--dropdown-" + position, font && "ln-c-list-group--dropdown-" + font),
      open: typeof isOpen !== 'undefined' ? isOpen : defaultOpen,
      onDropdownBlur: onClose,
      onItemClick: onItemClick
    })));
  };

  return ListGroupDropdown;
}(Component);

ListGroupDropdown.propTypes = {
  element: PropTypes.elementType,
  children: PropTypes.node,
  className: PropTypes.string,

  /** ClassName to be applied to the wrapping `li`  */
  containerClassName: PropTypes.string,

  /** Should the item be highlighted within the list */
  active: PropTypes.bool,

  /** Handle dropdown close event on outside click */
  onClose: PropTypes.func,

  /** Handler for setting the hover state if required */
  onMouseEnter: PropTypes.func,

  /** Handler for removing the hover state if required */
  onMouseLeave: PropTypes.func,

  /** Whether the dropdown is currently hovered if you wish for it to open on mouse over  */
  hover: PropTypes.bool,

  /** Disables the arrow icon from showing */
  noIcon: PropTypes.bool,

  /** Alignment of the dropdown menu in relation to the trigger */
  position: PropTypes.oneOf(['left', 'right']),

  /** Type style to use for the ListItems */
  font: PropTypes.oneOf(['body', 'button']),

  /** Whether the dropdown should be expanded by default */
  defaultOpen: PropTypes.bool,

  /** See ListGroup */
  onItemClick: PropTypes.func
};
ListGroupDropdown.defaultProps = {
  element: 'button',
  children: undefined,
  className: undefined,
  containerClassName: undefined,
  active: undefined,
  onClose: function onClose() {},
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  hover: undefined,
  noIcon: false,
  position: undefined,
  font: undefined,
  defaultOpen: false,
  onItemClick: undefined
};
ListGroupDropdown.displayName = 'ListGroupDropdown';
export { List, ListItem, ListGroup, ListGroupDropdown, ListGroupItem };